import { RectilinearView } from 'marzipano';
import { inject } from '@angular/core';
import { LoggingService } from '@newroom-connect/library/services';
import { ISphericalCoords, IScreenCoords, IViewportBounds } from '@newroom-connect/library/interfaces';

import { CoordinateValidationHelper } from '../helpers/coordinate-validation.helper';
import { ViewportHelper } from '../helpers/viewport.helper';

/**
 * Specialized coordinate system manager for Marzipano scenes.
 * Handles all coordinate conversions between screen and spherical coordinates.
 * Uses static helpers for validation and viewport operations.
 */
export class MarzipanoCoordinateSystem {
  private readonly logger = inject(LoggingService);

  private view: RectilinearView | null = null;
  private viewportBounds: IViewportBounds = { width: 0, height: 0 };

  /**
   * Initialize the coordinate system with a Marzipano view.
   * @param view - The Marzipano RectilinearView instance.
   * @param element - The HTML element containing the scene.
   */
  public initialize(view: RectilinearView, element: HTMLElement): void {
    this.view = view;
    this.viewportBounds = ViewportHelper.getViewportBounds(element);
  }

  /**
   * Convert screen coordinates to spherical coordinates.
   * @param screenCoords - Screen coordinates to convert.
   * @returns Spherical coordinates or null if conversion fails.
   */
  public screenToSpherical(screenCoords: IScreenCoords): ISphericalCoords | null {
    if (!this.isInitialized() || !this.view) {
      this.logger.warn('🔄 Coordinate system not initialized');
      return null;
    }

    if (!CoordinateValidationHelper.isValidScreenCoords(screenCoords)) {
      this.logger.warn('Invalid screen coordinates:', screenCoords);
      return null;
    }

    if (!CoordinateValidationHelper.isWithinViewport(screenCoords, this.viewportBounds)) {
      this.logger.warn('Screen coordinates outside viewport:', screenCoords);
      return null;
    }

    try {
      const sphericalCoords = this.view.screenToCoordinates(screenCoords);

      if (!CoordinateValidationHelper.isValidSphericalCoords(sphericalCoords)) {
        this.logger.warn('Invalid spherical coordinates result:', sphericalCoords);
        return null;
      }

      return sphericalCoords;
    } catch (error) {
      this.logger.error('Error converting screen to spherical coordinates:', error);
      return null;
    }
  }

  /**
   * Convert spherical coordinates to screen coordinates.
   *
   * @param sphericalCoords - Spherical coordinates to convert.
   *
   * @returns Screen coordinates or null if conversion fails.
   */
  public sphericalToScreen(sphericalCoords: ISphericalCoords): IScreenCoords | null {
    if (!this.isInitialized() || !this.view) {
      return null;
    }

    if (!CoordinateValidationHelper.isValidSphericalCoords(sphericalCoords)) {
      return null;
    }

    try {
      const screenCoords = this.view.coordinatesToScreen({ x: sphericalCoords.yaw, y: sphericalCoords.pitch });

      if (!CoordinateValidationHelper.isValidScreenCoords(screenCoords)) {
        return null;
      }

      return screenCoords;
    } catch (error) {
      return null;
    }
  }

  /**
   * Clamp screen coordinates to viewport bounds.
   * @param screenCoords - Screen coordinates to clamp.
   * @returns Clamped screen coordinates.
   */
  public clampToViewport(screenCoords: IScreenCoords): IScreenCoords {
    return CoordinateValidationHelper.clampToViewport(screenCoords, this.viewportBounds);
  }

  /**
   * Check if screen coordinates are within viewport bounds.
   * @param screenCoords - Screen coordinates to check.
   * @returns True if coordinates are within viewport.
   */
  public isWithinViewport(screenCoords: IScreenCoords): boolean {
    return CoordinateValidationHelper.isWithinViewport(screenCoords, this.viewportBounds);
  }

  /**
   * Get current viewport bounds.
   * @returns Current viewport bounds.
   */
  public getViewportBounds(): IViewportBounds {
    return { ...this.viewportBounds };
  }

  /**
   * Update internal viewport bounds based on the current container element.
   * Ensures subsequent in-viewport checks use up-to-date dimensions.
   *
   * @param element The scene container element.
   *
   * @returns The updated viewport bounds.
   */
  public updateViewportBounds(element: HTMLElement): IViewportBounds {
    this.viewportBounds = ViewportHelper.getViewportBounds(element);

    return { ...this.viewportBounds };
  }

  /**
   * Get current viewport aspect ratio.
   * @returns Viewport aspect ratio.
   */
  public getAspectRatio(): number {
    return ViewportHelper.getAspectRatio(this.viewportBounds);
  }

  /**
   * Check if the coordinate system is properly initialized.
   * @returns True if initialized and ready for use.
   */
  public isInitialized(): boolean {
    return this.view !== null && ViewportHelper.isValidViewportBounds(this.viewportBounds);
  }

  /**
   *
   */
  public reset(): void {
    this.view = null;
    this.viewportBounds = { width: 0, height: 0 };
  }
}
